/**
 * YOLO Detector using ONNX Runtime Web (WebGPU backend).
 *
 * Input: [1, 3, H, W] NCHW format, normalized 0-1
 * Output: [1, 300, 6] end2end format [x1, y1, x2, y2, score, class_id]
 */

import * as ort from 'onnxruntime-web/all';
import { SimplePreprocessor } from './simplePreprocessor.js';

// Serve ORT wasm/mjs from the same origin so `Cross-Origin-Embedder-Policy:
// require-corp` works on Safari — CDN files lack the CORP header. See
// public/ort/ (populated from node_modules/onnxruntime-web/dist).
ort.env.wasm.wasmPaths = '/ort/';

// This model's WebGPU EP is missing Conv/MaxPool kernels (verified via verbose
// GetCapability logs), so "webgpu" silently falls back to WASM per-op with
// heavy GPU↔CPU shuttles — measured 17 FPS vs 45 FPS for pure WASM. Use every
// core for SIMD-threaded WASM instead.
if (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) {
  ort.env.wasm.numThreads = Math.min(navigator.hardwareConcurrency, 8);
}
if (ort.env.webgpu) {
  ort.env.webgpu.powerPreference = 'high-performance';
}

const DEFAULT_INPUT_SIZE = 320;
const DEFAULT_CONFIDENCE_THRESHOLD = 0.15;
const DEFAULT_IOU_THRESHOLD = 0.45;

// YOLO26 end2end output: [batch, max_det, 6] where 6 = [x1, y1, x2, y2, score, class]
const YOLO26_OUTPUT_FEATURES = 6;

/**
 * ONNX YOLO26 Detector
 */
export class OnnxYoloDetector {
  constructor(modelPath, options = {}) {
    this.modelPath = modelPath;
    this.inputSize = options.inputSize || DEFAULT_INPUT_SIZE;
    this.confThreshold = options.confThreshold || DEFAULT_CONFIDENCE_THRESHOLD;
    this.iouThreshold = options.iouThreshold || DEFAULT_IOU_THRESHOLD;
    this.classNames = options.classNames || ['ball', 'paddle'];

    this.session = null;
    this.isInitialized = false;
    this.preprocessor = null;

    this.modelInfo = {
      modelName: null,
      deployedAt: null
    };

    this._detectCount = 0;
  }

  async initialize(timeout = 30000) {
    if (this.isInitialized) return true;

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`Model initialization timed out after ${timeout}ms`)), timeout);
    });

    await Promise.race([
      this._doInitialize(),
      timeoutPromise
    ]);
    return true;
  }

  async _doInitialize() {
    const modelDir = this.modelPath.replace(/\/[^/]+$/, '');
    let actualModelUrl = this.modelPath;

    try {
      let manifest = null;
      const unifiedUrl = `${modelDir}/manifest.detector.json?_=${Date.now()}`;
      const unifiedResp = await fetch(unifiedUrl, { cache: 'no-store' });
      if (unifiedResp.ok) {
        manifest = await unifiedResp.json();
      } else {
        const onnxUrl = `${modelDir}/manifest.onnx.detector.json?_=${Date.now()}`;
        const onnxResp = await fetch(onnxUrl, { cache: 'no-store' });
        if (onnxResp.ok) {
          manifest = await onnxResp.json();
        }
      }

      if (manifest && manifest.onnxModel) {
        actualModelUrl = `${modelDir}/${manifest.onnxModel}`;
        this.modelInfo = {
          modelName: manifest.modelName || null,
          deployedAt: manifest.deployedAt || null
        };
        if (manifest.inputSize) {
          this.inputSize = manifest.inputSize;
        }
      }
    } catch (_e) {
      // Use default path
    }

    this._modelUrl = actualModelUrl;

    console.log('[ONNX] Loading model from:', actualModelUrl);
    console.log(`[ONNX] Input size: ${this.inputSize}x${this.inputSize}`);

    this.preprocessor = new SimplePreprocessor(this.inputSize);
    await this.preprocessor.initialize();

    await this._createSession();
    this.isInitialized = true;
    return true;
  }

  async _createSession() {
    if (!navigator.gpu) {
      throw new Error('WebGPU not available');
    }

    const adapter = await navigator.gpu.requestAdapter();
    if (!adapter) {
      throw new Error('Failed to get WebGPU adapter');
    }

    const hasF16 = adapter.features.has('shader-f16');
    const info = await adapter.requestAdapterInfo?.() || {};
    console.log(`[ONNX] WebGPU adapter: ${info.vendor || 'unknown'} ${info.architecture || ''}, f16: ${hasF16}`);

    const params = typeof location !== 'undefined'
      ? new URLSearchParams(location.search) : new URLSearchParams();
    const debugPartition = params.get('partition') === '1';
    // WebGPU is default again — model was downgraded from opset 22 → 19, which
    // works around microsoft/onnxruntime issue: JS EP fails Conv kernel lookup
    // for opset 22 nodes (empirically verified: opset 17/19/21 works, 22 misses
    // all Conv). Override with ?backend=wasm for A/B benchmarking.
    const backend = params.get('backend') || 'webgpu';
    const layout = params.get('layout') || 'NHWC';
    const validation = params.get('validation') || 'wgpuOnly';
    const opt = params.get('opt') || 'all';

    let eps;
    if (backend === 'webgpu') {
      eps = [{ name: 'webgpu', preferredLayout: layout, validationMode: validation }];
    } else if (backend === 'mixed') {
      eps = [{ name: 'webgpu', preferredLayout: layout, validationMode: validation }, 'wasm'];
    } else {
      eps = ['wasm'];
    }
    const execMode = params.get('exec') || 'sequential';
    const interOp = parseInt(params.get('inter') || '0', 10);
    const intraOp = parseInt(params.get('intra') || '0', 10);
    this.session = await ort.InferenceSession.create(this._modelUrl, {
      executionProviders: eps,
      graphOptimizationLevel: opt,
      executionMode: execMode,
      ...(interOp ? { interOpNumThreads: interOp } : {}),
      ...(intraOp ? { intraOpNumThreads: intraOp } : {}),
      enableCpuMemArena: true,
      enableMemPattern: true,
      ...(debugPartition ? { logSeverityLevel: 0, logVerbosityLevel: 1 } : {})
    });
    console.log('[ONNX] Session created with WebGPU backend');
  }

  async detect(source) {
    if (!this.isInitialized) {
      throw new Error('Detector not initialized. Call initialize() first.');
    }

    let inputTensor = null;
    let results = null;

    try {
      const t0 = performance.now();

      inputTensor = await this.preprocessor.preprocess(source);
      const t1 = performance.now();

      results = await this.session.run({ images: inputTensor });
      const t2 = performance.now();

      const output = results.detections || results.output || results[Object.keys(results)[0]];
      const outputData = output.data;
      const shape = output.dims;
      const t3 = performance.now();

      this._detectCount++;

      if (!this._timingHistory) {
        this._timingHistory = { prep: [], infer: [], total: [] };
      }
      this._timingHistory.prep.push(t1 - t0);
      this._timingHistory.infer.push(t2 - t1);
      this._timingHistory.total.push(t3 - t0);

      if (this._timingHistory.prep.length > 100) {
        this._timingHistory.prep.shift();
        this._timingHistory.infer.shift();
        this._timingHistory.total.shift();
      }

      if (this._detectCount % 30 === 0) {
        const totalMs = t3 - t0;
        const fps = 1000 / totalMs;

        const history = this._timingHistory.total;
        let trend = '';
        if (history.length >= 60) {
          const first30 = history.slice(0, 30).reduce((a, b) => a + b) / 30;
          const last30 = history.slice(-30).reduce((a, b) => a + b) / 30;
          const change = ((last30 - first30) / first30 * 100).toFixed(0);
          trend = ` | trend: ${change > 0 ? '+' : ''}${change}%`;
        }

        let memInfo = '';
        if (performance.memory) {
          const used = (performance.memory.usedJSHeapSize / 1024 / 1024).toFixed(1);
          const total = (performance.memory.totalJSHeapSize / 1024 / 1024).toFixed(1);
          memInfo = ` | JSHeap: ${used}/${total}MB`;
        }

        console.log(`[ONNX] Frame ${this._detectCount}: prep=${(t1-t0).toFixed(1)}ms, infer=${(t2-t1).toFixed(1)}ms, total=${totalMs.toFixed(1)}ms (${fps.toFixed(1)} FPS)${trend}${memInfo}`);
      }

      const letterboxParams = this.preprocessor.getLetterboxParams();
      return this.postprocess(outputData, shape, letterboxParams, source);
    } catch (error) {
      console.error('[ONNX] Detection error:', error);

      if (error.message?.includes('destroyed') ||
          error.message?.includes('lost') ||
          error.message?.includes('context')) {
        console.error('[ONNX] WebGPU context may be lost.');
        this.isInitialized = false;
      }

      return [];
    } finally {
      if (inputTensor) {
        inputTensor.dispose();
      }
      if (results) {
        for (const key of Object.keys(results)) {
          if (results[key] && typeof results[key].dispose === 'function') {
            results[key].dispose();
          }
        }
      }
    }
  }

  /**
   * Postprocess YOLO26 end2end output
   * Output format: [batch, max_det, 6] where 6 = [x1, y1, x2, y2, score, class_id]
   */
  postprocess(data, shape, letterboxParams, source) {
    const [_batch, maxDet, _features] = shape;
    const { scale, offsetX, offsetY } = letterboxParams;
    const srcWidth = source.videoWidth || source.width;
    const srcHeight = source.videoHeight || source.height;

    if (this._detectCount % 60 === 1) {
      console.log(`[ONNX DEBUG] postprocess: shape=${JSON.stringify(shape)}, letterbox={scale:${scale.toFixed(3)}, offsetX:${offsetX.toFixed(1)}, offsetY:${offsetY.toFixed(1)}}, source=${srcWidth}x${srcHeight}`);
    }

    const detections = [];

    let maxBallScore = 0;
    let maxPaddleScore = 0;

    for (let i = 0; i < maxDet; i++) {
      const baseIdx = i * YOLO26_OUTPUT_FEATURES;
      const x1_input = data[baseIdx + 0];
      const y1_input = data[baseIdx + 1];
      const x2_input = data[baseIdx + 2];
      const y2_input = data[baseIdx + 3];
      const score = data[baseIdx + 4];
      const classId = Math.round(data[baseIdx + 5]);

      if (classId === 0 && score > maxBallScore) maxBallScore = score;
      if (classId === 1 && score > maxPaddleScore) maxPaddleScore = score;

      if (score < this.confThreshold) continue;

      const x1 = (x1_input - offsetX) / scale;
      const y1 = (y1_input - offsetY) / scale;
      const x2 = (x2_input - offsetX) / scale;
      const y2 = (y2_input - offsetY) / scale;

      const bbox = {
        x: Math.max(0, Math.min(srcWidth, x1)),
        y: Math.max(0, Math.min(srcHeight, y1)),
        width: Math.max(0, Math.min(srcWidth - x1, x2 - x1)),
        height: Math.max(0, Math.min(srcHeight - y1, y2 - y1))
      };

      if (bbox.width <= 0 || bbox.height <= 0) continue;

      detections.push({
        class: this.classNames[classId] || `class_${classId}`,
        classIndex: classId,
        confidence: score,
        bbox
      });
    }

    // YOLO26 one2one head already does NMS, just sort by confidence
    detections.sort((a, b) => b.confidence - a.confidence);

    if (this._detectCount % 60 === 1 || (detections.length > 0 && this._detectCount % 30 === 1)) {
      const summary = detections.map(d => `${d.class}:${(d.confidence*100).toFixed(0)}%@(${d.bbox.x.toFixed(0)},${d.bbox.y.toFixed(0)},${d.bbox.width.toFixed(0)}x${d.bbox.height.toFixed(0)})`).join(', ');
      console.log(`[ONNX DEBUG] Frame ${this._detectCount}: ${detections.length} detections: ${summary || 'none'}`);
      if (detections.length === 0) {
        console.log(`[ONNX DEBUG] Frame ${this._detectCount}: No detections above ${(this.confThreshold*100).toFixed(0)}% threshold. Max scores: ball=${(maxBallScore*100).toFixed(1)}%, paddle=${(maxPaddleScore*100).toFixed(1)}%`);
      }
    }

    return detections;
  }

  getModelInfo() {
    return this.modelInfo;
  }

  async stop() {
    if (this.session) {
      await this.session.release();
      this.session = null;
    }
    if (this.preprocessor) {
      await this.preprocessor.dispose();
    }
    this.isInitialized = false;
  }
}

export function createDetector(options = {}) {
  const modelPath = options.modelPath || '/pickleball-model/pickleball.onnx';
  return new OnnxYoloDetector(modelPath, {
    classNames: options.classNames || ['ball', 'paddle'],
    inputSize: options.inputSize || 320,
    confThreshold: options.confThreshold || 0.15,
    iouThreshold: options.iouThreshold || 0.45
  });
}

export default OnnxYoloDetector;
